import { useEffect, useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FiDownload, FiShare } from 'react-icons/fi';

const InstallPrompt = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        // Check if it's already in standalone mode (already installed).
        const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches 
            || (window.navigator as any).standalone 
            || document.referrer.includes('android-app://');
        
        setIsStandalone(isInStandaloneMode);

        // Check if iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        setIsIOS(isIOSDevice);

        // Check if this session has already been displayed
        const hasSeenPrompt = sessionStorage.getItem('installPromptShown');
        
        if (!isInStandaloneMode && !hasSeenPrompt) {
            // For Android/Chrome
            const handleBeforeInstallPrompt = (e: Event) => {
                e.preventDefault();
                setDeferredPrompt(e);
                setShowPrompt(true);
            };

            window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

            // For iOS, Display after 3 seconds if not in standalone mode
            if (isIOSDevice) {
                setTimeout(() => {
                    setShowPrompt(true);
                }, 3000);
            }

            return () => {
                window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            };
        }
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            
            if (outcome === 'accepted') {
                setDeferredPrompt(null);
            }
        }
        handleClose();
    };

    const handleClose = () => {
        setShowPrompt(false);
        sessionStorage.setItem('installPromptShown', 'true');
    };

    if (!showPrompt || isStandalone) return null;

    return (
        <div className="fixed bottom-20 left-4 right-4 z-[100] animate-slide-up">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl shadow-2xl p-4 max-w-md mx-auto">
                <button
                    onClick={handleClose}
                    className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-full transition-colors"
                >
                    <IoMdClose size={20} />
                </button>

                <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl">📱</span>
                    </div>
                    
                    <div className="flex-1 pt-1">
                        <h3 className="font-bold text-lg mb-1">Installer l'application</h3>
                        <p className="text-sm text-blue-100 mb-3">
                            {isIOS 
                                ? "Accédez rapidement à votre espace de commande depuis votre écran d'accueil"
                                : "Installez l'application pour une meilleure expérience"
                            }
                        </p>

                        {isIOS ? (
                            <div className="bg-white/10 rounded-lg p-3 text-sm">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xl">1.</span>
                                    <span>Appuyez sur le bouton partage</span>
                                    <FiShare className="inline" />
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xl">2.</span>
                                    <span>Puis "Sur l'écran d'accueil"</span>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={handleInstallClick}
                                className="w-full bg-white text-blue-600 py-2.5 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors"
                            >
                                <FiDownload size={18} />
                                Installer maintenant
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InstallPrompt;